/**
 * Access Requests — Super Admin Only
 *
 * Manage who can access the ClassRMM dashboard.
 * - View pending access requests (submitted from the login page)
 * - Approve → generates a token → copy and send to the person
 * - Reject requests
 * - View and revoke all issued tokens
 * - Create tokens directly without a request form
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../store/auth';
import toast from 'react-hot-toast';

const API = process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API || '';

const STATUS_COLORS = {
  pending:  '#eab308',
  approved: 'var(--green)',
  rejected: 'var(--red)',
};

export default function AccessRequests() {
  const { authHeaders } = useAuth();
  const [tab, setTab]         = useState('requests');
  const [requests, setRequests] = useState([]);
  const [tokens, setTokens]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [generated, setGenerated] = useState(null);  // {token, name, email}

  // Direct-create token form state
  const [dcName, setDcName]   = useState('');
  const [dcEmail, setDcEmail] = useState('');
  const [dcExpiry, setDcExpiry] = useState('');   // expires_in_days
  const [approveExpiry, setApproveExpiry] = useState('');  // for approve modal

  // Inline dialog state (replaces window.prompt/confirm — both are disabled in Electron)
  const [dialog, setDialog] = useState(null);
  // dialog = { type:'prompt'|'confirm', message, value, resolve }

  const showPrompt = (message, defaultValue = '') => new Promise(resolve => {
    setDialog({ type: 'prompt', message, value: defaultValue, resolve });
  });
  const showConfirm = (message) => new Promise(resolve => {
    setDialog({ type: 'confirm', message, value: '', resolve });
  });
  const closeDialog = (result) => {
    if (dialog) dialog.resolve(result);
    setDialog(null);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rRes, tRes] = await Promise.all([
        fetch(`${API}/api/access/requests`, { headers: authHeaders() }),
        fetch(`${API}/api/access/tokens`,   { headers: authHeaders() }),
      ]);
      if (rRes.status === 403) {
        toast.error('Super admin access required');
        return;
      }
      setRequests(await rRes.json());
      setTokens(await tRes.json());
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    const days = await showPrompt('Token expiration in days (leave empty for no expiration):');
    if (days === null) return;
    const body = {};
    if (days && parseInt(days) > 0) body.expires_in_days = parseInt(days);
    try {
      const res = await fetch(`${API}/api/access/requests/${id}/approve`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setGenerated(data);
      toast.success(`Token generated for ${data.name}`);
      load();
    } catch (e) {
      toast.error(e.message || 'Approve failed');
    }
  };

  const reject = async (id) => {
    if (!(await showConfirm('Reject this request?'))) return;
    try {
      await fetch(`${API}/api/access/requests/${id}/reject`, {
        method: 'POST', headers: authHeaders(),
      });
      toast.success('Request rejected');
      load();
    } catch {
      toast.error('Reject failed');
    }
  };

  const revoke = async (id, name) => {
    if (!(await showConfirm(`Revoke token for "${name}"? They will be locked out immediately.`))) return;
    try {
      await fetch(`${API}/api/access/tokens/${id}/revoke`, {
        method: 'POST', headers: authHeaders(),
      });
      toast.success(`Token revoked for ${name}`);
      load();
    } catch {
      toast.error('Revoke failed');
    }
  };

  const ban = async (id, name) => {
    if (!(await showConfirm(`Permanently BAN "${name}"? They will be locked out and cannot use this token again.`))) return;
    try {
      await fetch(`${API}/api/access/tokens/${id}/ban`, { method: 'POST', headers: authHeaders() });
      toast.success(`${name} banned`);
      load();
    } catch { toast.error('Ban failed'); }
  };

  const unban = async (id, name) => {
    if (!(await showConfirm(`Unban "${name}"? Their token will become active again.`))) return;
    try {
      await fetch(`${API}/api/access/tokens/${id}/unban`, { method: 'POST', headers: authHeaders() });
      toast.success(`${name} unbanned`);
      load();
    } catch { toast.error('Unban failed'); }
  };

  const deleteToken = async (id, name) => {
    if (!(await showConfirm(`PERMANENTLY DELETE token for "${name}"? This cannot be undone.`))) return;
    try {
      await fetch(`${API}/api/access/tokens/${id}`, { method: 'DELETE', headers: authHeaders() });
      toast.success(`Token for ${name} deleted`);
      load();
    } catch { toast.error('Delete failed'); }
  };

  const setExpiration = async (id, name) => {
    const days = await showPrompt(`Set expiration for "${name}" (days from now, empty to remove expiration):`);
    if (days === null) return; // cancelled
    const body = { expires_in_days: days ? parseInt(days) : null };
    try {
      const res = await fetch(`${API}/api/access/tokens/${id}/set-expiration`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      toast.success(data.expires_at ? `Expires: ${new Date(data.expires_at).toLocaleDateString()}` : 'Expiration removed');
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const createDirect = async (e) => {
    e.preventDefault();
    const body = { name: dcName, email: dcEmail };
    if (dcExpiry && parseInt(dcExpiry) > 0) body.expires_in_days = parseInt(dcExpiry);
    try {
      const res = await fetch(`${API}/api/access/create-token`, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail);
      setGenerated(data);
      setDcName(''); setDcEmail(''); setDcExpiry('');
      toast.success(`Token created for ${data.name}`);
      load();
    } catch (e) {
      toast.error(e.message || 'Failed');
    }
  };

  const copy = (t) => navigator.clipboard.writeText(t).then(() => toast.success('Copied!'));

  const pending  = requests.filter(r => r.status === 'pending');
  const reviewed = requests.filter(r => r.status !== 'pending');

  return (
    <div>
      {/* Page header */}
      <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        <h2 style={{ fontWeight:700 }}>🔐 Access Requests</h2>
        {pending.length > 0 && (
          <span style={{ background:'#ef444422', border:'1px solid var(--red)',
                         color:'var(--red)', borderRadius:12, padding:'2px 10px',
                         fontSize:12, fontWeight:700 }}>
            {pending.length} pending
          </span>
        )}
        <button onClick={load} style={{ ...btn(), marginLeft:'auto' }}>↻ Refresh</button>
      </div>

      {/* Generated token banner */}
      {generated && (
        <div style={{ background:'#22c55e15', border:'1px solid var(--green)',
                      borderRadius:10, padding:20, marginBottom:20 }}>
          <div style={{ fontWeight:700, color:'var(--green)', marginBottom:6 }}>
            ✅ Tokens ready for {generated.name}
          </div>
          <p style={{ color:'var(--muted)', fontSize:12, marginBottom:14 }}>
            Send <strong>{generated.email || generated.name}</strong> both tokens below.
          </p>

          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4,
                          textTransform:'uppercase', letterSpacing:.5 }}>
              Dashboard Login Token — paste on the "Login with Token" tab
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center' }}>
              <code style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)',
                             borderRadius:6, padding:'8px 12px', fontSize:12,
                             color:'var(--green)', wordBreak:'break-all' }}>
                {generated.token}
              </code>
              <button onClick={() => copy(generated.token)} style={btn('var(--green)')}>Copy</button>
            </div>
          </div>

          {generated.agent_token && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, color:'var(--muted)', marginBottom:4,
                            textTransform:'uppercase', letterSpacing:.5 }}>
                Agent Build Token — set as AGENT_TOKEN in build.cfg (devices registered with this token only show in their dashboard)
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <code style={{ flex:1, background:'var(--bg)', border:'1px solid var(--border)',
                               borderRadius:6, padding:'8px 12px', fontSize:12,
                               color:'#f59e0b', wordBreak:'break-all' }}>
                  {generated.agent_token}
                </code>
                <button onClick={() => copy(generated.agent_token)} style={btn('#f59e0b')}>Copy</button>
              </div>
            </div>
          )}

          <button onClick={() => setGenerated(null)} style={{ ...btn(), marginTop:6, fontSize:11 }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:'flex', gap:4, borderBottom:'1px solid var(--border)',
                    paddingBottom:8, marginBottom:20 }}>
        {[
          ['requests', `Requests (${requests.length})`],
          ['tokens',   `Issued Tokens (${tokens.length})`],
          ['create',   '+ Create Token Directly'],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
                  style={{ ...btn(tab === key ? 'var(--accent)' : undefined) }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? <p style={{ color:'var(--muted)' }}>Loading…</p> : (

        tab === 'requests' ? (
          <div>
            {pending.length > 0 && (
              <Section title="Pending Approval">
                {pending.map(r => (
                  <RequestCard key={r.id} r={r}
                    onApprove={() => approve(r.id)}
                    onReject={() => reject(r.id)} />
                ))}
              </Section>
            )}
            {reviewed.length > 0 && (
              <Section title="Reviewed">
                {reviewed.map(r => <RequestCard key={r.id} r={r} />)}
              </Section>
            )}
            {requests.length === 0 && (
              <Empty text="No access requests yet. New admins submit requests from the login page." />
            )}
          </div>

        ) : tab === 'tokens' ? (
          <div>
            {tokens.length === 0 ? (
              <Empty text="No tokens issued yet." />
            ) : (
              <table style={tableStyle}>
                <thead>
                  <tr>
                    {['Name', 'Email', 'Issued By', 'Expires', 'Last Used', 'Login Token', 'Agent Token', 'Status', 'Actions'].map(h => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tokens.map(t => {
                    const statusColor = t.banned ? '#dc2626' : t.expired ? '#f59e0b' : t.revoked ? 'var(--red)' : 'var(--green)';
                    const statusText = t.banned ? '⛔ Banned' : t.expired ? '⏰ Expired' : t.revoked ? '✗ Revoked' : '✓ Active';
                    return (
                    <tr key={t.id} style={{ borderBottom:'1px solid var(--border)' }}>
                      <td style={tdStyle}>{t.name}</td>
                      <td style={{ ...tdStyle, color:'var(--muted)', fontSize:12 }}>{t.email || '—'}</td>
                      <td style={{ ...tdStyle, color:'var(--muted)', fontSize:12 }}>{t.issued_by}</td>
                      <td style={{ ...tdStyle, fontSize:11 }}>
                        {t.expires_at ? (
                          <span style={{ color: t.expired ? '#f59e0b' : 'var(--muted)' }}>
                            {new Date(t.expires_at).toLocaleDateString()}
                            {t.expired && ' (expired)'}
                          </span>
                        ) : <span style={{ color:'var(--muted)' }}>Never</span>}
                      </td>
                      <td style={{ ...tdStyle, fontSize:12 }}>
                        {t.last_used_at ? new Date(t.last_used_at).toLocaleString() : 'Never'}
                      </td>
                      <td style={{ ...tdStyle, fontSize:11, fontFamily:'monospace', maxWidth:160 }}>
                        {t.login_token ? (
                          <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ color:'var(--green)', wordBreak:'break-all',
                                           overflow:'hidden', textOverflow:'ellipsis',
                                           maxWidth:100, display:'inline-block', whiteSpace:'nowrap' }}
                                  title={t.login_token}>
                              {t.login_token.slice(0, 12)}…
                            </span>
                            <button onClick={() => copy(t.login_token)}
                              style={{ ...btn('var(--green)'), padding:'2px 8px', fontSize:10, flexShrink:0 }}>
                              Copy
                            </button>
                          </span>
                        ) : <span style={{ color:'var(--muted)' }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, fontSize:11, fontFamily:'monospace', maxWidth:160 }}>
                        {t.agent_token ? (
                          <span style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <span style={{ color:'#f59e0b', wordBreak:'break-all',
                                           overflow:'hidden', textOverflow:'ellipsis',
                                           maxWidth:100, display:'inline-block', whiteSpace:'nowrap' }}
                                  title={t.agent_token}>
                              {t.agent_token.slice(0, 12)}…
                            </span>
                            <button onClick={() => copy(t.agent_token)}
                              style={{ ...btn('#f59e0b'), padding:'2px 8px', fontSize:10, flexShrink:0 }}>
                              Copy
                            </button>
                          </span>
                        ) : <span style={{ color:'var(--muted)' }}>—</span>}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: statusColor, fontWeight:600, fontSize:12 }}>
                          {statusText}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, whiteSpace:'nowrap' }}>
                        <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
                          {!t.revoked && !t.banned && (
                            <button onClick={() => revoke(t.id, t.name)} style={btn('var(--red)')}>
                              Revoke
                            </button>
                          )}
                          {!t.banned ? (
                            <button onClick={() => ban(t.id, t.name)}
                              style={{ ...btn('#dc2626'), fontSize:11 }}>
                              Ban
                            </button>
                          ) : (
                            <button onClick={() => unban(t.id, t.name)}
                              style={{ ...btn('var(--green)'), fontSize:11 }}>
                              Unban
                            </button>
                          )}
                          <button onClick={() => setExpiration(t.id, t.name)}
                            style={{ ...btn('#8b5cf6'), fontSize:11 }}>
                            Expiry
                          </button>
                          <button onClick={() => deleteToken(t.id, t.name)}
                            style={{ ...btn('#dc2626'), fontSize:11 }}>
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

        ) : (
          /* Create token directly */
          <div style={{ maxWidth:400 }}>
            <p style={{ color:'var(--muted)', fontSize:13, marginBottom:20, lineHeight:1.7 }}>
              Issue a token for someone directly — no request form needed.
              Useful when you want to onboard a specific person proactively.
            </p>
            <form onSubmit={createDirect}>
              <label style={labelStyle}>Full Name *</label>
              <input value={dcName} onChange={e => setDcName(e.target.value)} required
                     placeholder="e.g. John Doe" style={inputStyle} />
              <label style={labelStyle}>Email <span style={{color:'var(--muted)'}}>(optional)</span></label>
              <input value={dcEmail} onChange={e => setDcEmail(e.target.value)}
                     type="email" placeholder="e.g. john@school.edu"
                     style={inputStyle} />
              <label style={labelStyle}>Expiration <span style={{color:'var(--muted)'}}>(days, empty = never expires)</span></label>
              <input value={dcExpiry} onChange={e => setDcExpiry(e.target.value)}
                     type="number" min="1" placeholder="e.g. 30"
                     style={{ ...inputStyle, marginBottom:20 }} />
              <button type="submit" style={{ ...btn('var(--accent)'), padding:'9px 20px', fontSize:13 }}>
                Generate Token
              </button>
            </form>
          </div>
        )
      )}

      {/* Inline dialog — replaces window.prompt/confirm (both disabled in Electron) */}
      {dialog && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.65)',
                      display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}
             onClick={(e) => { if (e.target === e.currentTarget) closeDialog(null); }}>
          <div style={{ background:'var(--surface)', border:'1px solid var(--border)',
                        borderRadius:10, padding:24, minWidth:320, maxWidth:440, boxShadow:'0 8px 32px rgba(0,0,0,0.4)' }}>
            <p style={{ marginBottom:16, color:'var(--text)', lineHeight:1.5 }}>{dialog.message}</p>
            {dialog.type === 'prompt' && (
              <input
                autoFocus
                value={dialog.value}
                onChange={e => setDialog(d => ({ ...d, value: e.target.value }))}
                onKeyDown={e => {
                  if (e.key === 'Enter') closeDialog(dialog.value);
                  if (e.key === 'Escape') closeDialog(null);
                }}
                type="number" min="1" placeholder="e.g. 30"
                style={{ ...inputStyle, marginBottom:4 }}
              />
            )}
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end', marginTop:16 }}>
              <button onClick={() => closeDialog(null)} style={btn()}>Cancel</button>
              <button
                autoFocus={dialog.type === 'confirm'}
                onClick={() => closeDialog(dialog.type === 'confirm' ? true : dialog.value)}
                style={btn('var(--accent)')}>
                {dialog.type === 'confirm' ? 'Confirm' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function RequestCard({ r, onApprove, onReject }) {
  return (
    <div style={{ background:'var(--surface)', border:'1px solid var(--border)',
                  borderRadius:8, padding:16, marginBottom:10,
                  display:'flex', justifyContent:'space-between', gap:16, flexWrap:'wrap' }}>
      <div>
        <div style={{ fontWeight:700, marginBottom:6 }}>{r.name}</div>
        <div style={{ color:'var(--muted)', fontSize:12, lineHeight:2 }}>
          📧 {r.email}<br />
          💬 {r.telegram_username ? `@${r.telegram_username}` : '—'}<br />
          📅 {r.submitted_at ? new Date(r.submitted_at).toLocaleString() : '—'}
          {r.reviewed_by && <><br />✅ Reviewed by: {r.reviewed_by}</>}
        </div>
      </div>
      <div style={{ display:'flex', flexDirection:'column', alignItems:'flex-end', gap:6 }}>
        <span style={{ fontSize:11, fontWeight:700, textTransform:'uppercase', letterSpacing:1,
                       color: STATUS_COLORS[r.status] || 'var(--muted)' }}>
          {r.status}
        </span>
        {r.status === 'pending' && onApprove && (
          <>
            <button onClick={onApprove} style={btn('var(--green)')}>✓ Approve & Generate Token</button>
            <button onClick={onReject}  style={btn('var(--red)')}>✗ Reject</button>
          </>
        )}
        {r.status === 'approved' && onApprove && (
          <button onClick={onApprove} style={btn()}>Show Token Again</button>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom:24 }}>
      <div style={{ fontSize:11, color:'var(--muted)', textTransform:'uppercase',
                    letterSpacing:.5, marginBottom:10 }}>{title}</div>
      {children}
    </div>
  );
}

function Empty({ text }) {
  return (
    <div style={{ color:'var(--muted)', fontSize:13, textAlign:'center',
                  padding:'40px 20px' }}>{text}</div>
  );
}

const btn = (color) => ({
  padding:'6px 12px',
  background: color ? `${color}22` : 'transparent',
  border: `1px solid ${color || 'var(--border)'}`,
  borderRadius:6, color: color || 'var(--muted)',
  cursor:'pointer', fontSize:12,
  fontWeight: color ? 600 : 400,
  whiteSpace:'nowrap',
});

const inputStyle = {
  display:'block', width:'100%', marginBottom:12, padding:'9px 12px',
  background:'var(--bg)', border:'1px solid var(--border)', borderRadius:6,
  color:'var(--text)', fontSize:13, boxSizing:'border-box',
};

const labelStyle = { display:'block', marginBottom:4, color:'var(--muted)', fontSize:12 };
const tableStyle = { width:'100%', borderCollapse:'collapse', fontSize:13 };
const thStyle    = { textAlign:'left', padding:'8px 12px', color:'var(--muted)',
                     fontSize:11, textTransform:'uppercase', letterSpacing:.5,
                     borderBottom:'1px solid var(--border)' };
const tdStyle    = { padding:'10px 12px', color:'var(--text)' };
